import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import type { DroneTelemetry } from '@shared/types';
import { useTelemetryAnimationFrame } from '../../../drone/hooks';

/** 从一帧遥测里取出要展示的文本与配色令牌（不含 `--` 前缀）。 */
export type LiveValueSelector = (telemetry: DroneTelemetry) => {
  text: string;
  tone?: string;
  /** 0-1 的进度比例；仅在 `progress` 为 true 时用于绘制进度条。 */
  progress?: number;
};

interface LiveValueProps {
  droneId: string | undefined;
  /** store 中的低频兜底值：链路断开或总线无新帧时仍要显示最后已知数据 */
  fallback: DroneTelemetry | null;
  select: LiveValueSelector;
  className?: string;
  suffix?: string;
  /** 是否在数值下方绘制进度条（宽度与配色都由 select 返回，逐帧直接改样式） */
  progress?: boolean;
  /**
   * 标称字号（px）。给出后数值会自动收缩字号以避免超出卡片边界
   * （例如 `31m22s`、`161.4°` 这类较长的读数）。
   */
  fitPx?: number;
}

/** 自动收缩的下限：最多缩到标称字号的 55%，再长就交给 `overflow-hidden` 兜底。 */
const MIN_FIT_RATIO = 0.55;

/**
 * 直连遥测总线的实时数值。
 *
 * 刻意不走 React 状态：每帧只改一个 DOM 文本节点，因此姿态/电量这类
 * 高刷新率数字可以在 20 Hz 下稳定渲染而不触发任何重渲染。
 *
 * 自动收缩字号只在文本或容器宽度变化时测量（1 Hz 的遥测不会带来布局抖动），
 * 保证任何读数都不会越过卡片边框，同时长读数也不会比短读数小太多。
 */
export function LiveValue({
  droneId,
  fallback,
  select,
  className = '',
  suffix,
  progress = false,
  fitPx,
}: LiveValueProps) {
  const rootRef = useRef<HTMLSpanElement>(null);
  const valueRef = useRef<HTMLSpanElement>(null);
  const suffixRef = useRef<HTMLSpanElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const selectRef = useRef(select);
  const fitRef = useRef(fitPx);
  selectRef.current = select;
  fitRef.current = fitPx;

  const fit = () => {
    const root = rootRef.current;
    const nominal = fitRef.current;
    if (!root || nominal === undefined) return;
    const available = root.clientWidth;
    if (available <= 0) return;
    let size = nominal;
    root.style.fontSize = `${size}px`;
    // 逐步收缩：后缀/margin 不随字号等比缩放的误差由第二轮测量修正
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const content = root.scrollWidth;
      if (content <= available) break;
      const next = Math.max(
        nominal * MIN_FIT_RATIO,
        Math.floor(((size * available) / content) * 10) / 10,
      );
      if (next >= size) break;
      size = next;
      root.style.fontSize = `${size}px`;
    }
  };

  const apply = (telemetry: DroneTelemetry) => {
    const node = valueRef.current;
    if (!node) return;
    const { text, tone, progress: ratio } = selectRef.current(telemetry);
    if (node.textContent !== text) {
      node.textContent = text;
      fit();
    }
    const color = tone ? `var(--${tone})` : '';
    if (node.style.color !== color) node.style.color = color;
    if (suffixRef.current) suffixRef.current.textContent = suffix ?? '';
    const bar = progressRef.current;
    if (bar) {
      const clamped = Math.min(1, Math.max(0, ratio ?? 0));
      const width = `${Math.round(clamped * 100)}%`;
      if (bar.style.width !== width) bar.style.width = width;
      if (bar.style.background !== color) bar.style.background = color;
    }
  };

  useTelemetryAnimationFrame(droneId, apply);

  useEffect(() => {
    // apply 里的 select 走 ref，因此这里只跟随兜底值变化
    if (fallback) apply(fallback);
  }, [fallback]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || fitPx === undefined) return;
    fit();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => fit());
    observer.observe(root);
    return () => observer.disconnect();
  }, [fitPx]);

  return (
    <span
      ref={rootRef}
      className={['block max-w-full overflow-hidden whitespace-nowrap', className].join(' ')}
    >
      <span ref={valueRef} className="tabular">
        --
      </span>
      {suffix ? (
        <span ref={suffixRef} className="ml-1 text-[0.6em] font-normal">
          {suffix}
        </span>
      ) : null}
      {progress ? (
        <span className="mt-1.5 block h-1.5 w-full overflow-hidden rounded-full bg-surface-3" aria-hidden>
          <span ref={progressRef} className="block h-full rounded-full" style={{ width: '0%' }} />
        </span>
      ) : null}
    </span>
  );
}

export interface BigMetricProps {
  label: ReactNode;
  droneId: string | undefined;
  telemetry: DroneTelemetry | null;
  select: LiveValueSelector;
  unit?: string;
  hint?: ReactNode;
  /** 数值字号：`lg` 用于基础指标行，`xl` 用于电量这类核心指标 */
  size?: 'md' | 'lg' | 'xl';
  emphasis?: boolean;
  /** 在数值下方绘制进度条（电量这类需要一眼看比例的指标） */
  progress?: boolean;
  className?: string;
}

const SIZE_CLASS = {
  md: 'text-2xl',
  lg: 'text-4xl',
  xl: 'text-5xl',
} as const;

/** 与 `SIZE_CLASS` 对应的标称字号（px），用于超宽时自动收缩（text-2xl=24 / text-4xl=36 / text-5xl=48）。 */
const SIZE_PX = {
  md: 24,
  lg: 36,
  xl: 48,
} as const;

/** 大数字指标块。实时页的核心读数都走这里，保证字号与对比度一致。 */
export function BigMetric({
  label,
  droneId,
  telemetry,
  select,
  unit,
  hint,
  size = 'lg',
  emphasis = false,
  progress = false,
  className = '',
}: BigMetricProps) {
  return (
    <div
      className={[
        'flex flex-col gap-0.5 rounded-lg border px-3 py-2.5',
        emphasis ? 'border-line-strong bg-surface-2' : 'border-line bg-surface-1',
        className,
      ].join(' ')}
    >
      <span className="text-[11px] font-medium tracking-wide text-fg-muted">{label}</span>
      <LiveValue
        droneId={droneId}
        fallback={telemetry}
        select={select}
        suffix={unit}
        progress={progress}
        fitPx={SIZE_PX[size]}
        className={[SIZE_CLASS[size], 'font-semibold leading-none'].join(' ')}
      />
      {hint ? <span className="text-[11px] leading-tight text-fg-subtle">{hint}</span> : null}
    </div>
  );
}
